import { Row, Col, CardBody, Card, Modal, Button, Alert } from "react-bootstrap";
import crudApi from "@serie3/common/api/crudApi";
import { useCallback, useEffect, useState } from "react";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import listHandler from "@serie3/common/Domain/listHandler";
import PostFormView from "./components/PostFormView";
import AddButton from "../shared/Buttons/AddButton";
import ToastMessages from "../../ToastMessages";
import { useTranslation } from "react-i18next";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import TabTitle from "../shared/Tabs/TabTitle";
import LoadingModal from "../shared/Modals/LoadingModal";
import VariantsModal from "./components/VariantsModal";
import TimnelineTab from "./components/TimnelineTab";
import PostAssistant from "./components/PostAssistant";
import TelepromterModal from "./components/TelepromterModal";
import ShareModal from "./components/ShareModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";

const Timeline = () => {
  const [posts, setPosts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [planned, setPlanned] = useState([]);
  const [unscheduled, setUnscheduled] = useState([]);
  const [published, setPublished] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editPost, setEditPost] = useState();
  const [showTeleprompter, setShowTelepromter] = useState(false);
  const [trigger, setTrigger] = useState(-1);
  const [showVariants, setShowVariants] = useState(false);
  const [showPosting, setShowPosting] = useState(false);
  const [showGenerating, setShowGenerating] = useState(false);
  const [showPostAssistant, setShowPostAssistant] = useState(false);
  const [activeKey, setActiveKey] = useState("content");
  const [showShare, setShowShare] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [showForceDelete, setShowForceDelete] = useState(false);
  const [forceDeleteArgs, setForceDeleteArgs] = useState(null);
  const [forceDeleteDetails, setForceDeleteDetails] = useState(null);

  const { t } = useTranslation();

  const sortPosts = (posts) => {
    const drafts = [];
    const inProgress = [];
    const unscheduled = [];

    posts.forEach((post) => {
      if (
        post.in_progress === true &&
        (!Array.isArray(post.schedules) || post.schedules.length === 0)
      ) {
        // Add to inProgress only if it's in progress, has no schedules, and is not ready
        inProgress.push(post);
      } else if (post.draft === true) {
        drafts.push(post); // Add to drafts if it's a draft and not in progress
      } else if (Array.isArray(post.schedules) && post.schedules.length === 0) {
        unscheduled.push(post); // Add to unscheduled if it has no schedules and isn't a draft/in progress
      }
    });

    setDrafts(drafts);
    setUnscheduled(unscheduled);
    setInProgress(inProgress);
  };

  useEffect(() => {
    const fetchPosts = async () => {
      await crudApi.getAll(
        SocialEndpoints.posts.index,
        sortPosts,
        SocialEndpoints.posts.dataField
      );
    };

    const fetchPlannedPosts = async () => {
      await crudApi.getAll(
        SocialEndpoints.posts.plannedOnly,
        setPlanned,
        SocialEndpoints.posts.scheduledDataField
      );
    };

    const fetchPulishedPosts = async () => {
      await crudApi.getAll(
        SocialEndpoints.posts.postedOnly,
        setPublished,
        SocialEndpoints.posts.scheduledDataField
      );
    };

    fetchPulishedPosts();
    fetchPlannedPosts();
    fetchPosts();
  }, [trigger]);

  const editAction = useCallback((post) => {
    setEditPost(post);
    setShowEdit(true);
  }, []);

  const postErrorFunction = useCallback(() => {
    let message = t("Backend.Post.ErrorPublishing");
    ToastMessages.Error(message);
  }, [t]);


  const deleteAction = useCallback(async (updateListFunction, dataList, index, postId) => {
    const deleteUrl = SocialEndpoints.posts.delete;
    const response = await crudApi.del(deleteUrl, postId);
    if (response.status === 409) {
      setForceDeleteArgs({ updateListFunction, dataList, index, postId });
      setForceDeleteDetails(response.data?.details ?? response.data?.error ?? null);
      setShowForceDelete(true);
      return;
    }
    if (response.ok) {
      dataList.splice(index, 1);
      updateListFunction([...dataList]);
    }
  }, []);

  const forceDeleteAction = useCallback(async () => {
    setShowForceDelete(false);
    if (!forceDeleteArgs) return;
    const { updateListFunction, dataList, index, postId } = forceDeleteArgs;
    const response = await crudApi.delForce(SocialEndpoints.posts.delete, postId);
    if (response.ok) {
      dataList.splice(index, 1);
      updateListFunction([...dataList]);
    }
  }, [forceDeleteArgs]);

  const publish = useCallback(async (postId) => {
    setShowPosting(true);
    const response = await crudApi.justGet(
      SocialEndpoints.posts.publish.linkedin + postId
    );
    let data = JSON.stringify(response.data);
    listHandler.updateList(
      response,
      data,
      posts,
      setPosts,
      postErrorFunction,
      true
    );
    if (response.ok) {
      let message = t("Backend.Post.SuccessfulPublished");
      ToastMessages.SuccessfulSave(message);
    } else {
      let message = t("Backend.Post.ErrorPublishing");
      ToastMessages.SuccessfulSave(message);
    }
    setShowPosting(false);
  }, [posts, postErrorFunction, t]);

  const setDraft = useCallback(async (post, list) => {
    const response = await crudApi.justGet(
      SocialEndpoints.posts.setDraft + post.id
    );
    let data = JSON.stringify(response.data);
    listHandler.updateList(
      response,
      data,
      posts,
      setPosts,
      postErrorFunction,
      true
    );
    if (response.ok) {
      const editetPost = JSON.parse(data);
      //reomove from draft list or inProgress list and move to unscheduled
      listHandler.deleteWithoutCall(post, list);

      if (editetPost.draft && editetPost.inProgress) {
        inProgress.unshift(editetPost);
      }

      if (editetPost.draft && !editetPost.inProgress) {
        drafts.unshift(editetPost);
      }

      if (!editetPost.draft) {
        unscheduled.unshift(editetPost);
      }

      let message = t("Backend.Post.SuccessfulPublished");
      ToastMessages.SuccessfulSave(message);
    }
  }, [posts, drafts, inProgress, unscheduled, postErrorFunction, t]);

  const markInProgress = useCallback(async (post, list) => {
    const response = await crudApi.justGet(
      SocialEndpoints.posts.markInProgress + post.id
    );
    let data = JSON.stringify(response.data);
    listHandler.updateList(
      response,
      data,
      posts,
      setPosts,
      postErrorFunction,
      true
    );

    if (response.ok) {
      listHandler.deleteWithoutCall(post, list);
      const editetPost = JSON.parse(data);
      if (editetPost.draft) {
        if (editetPost.in_progress) {
          inProgress.unshift(editetPost);
        } else {
          drafts.unshift(editetPost);
        }
      }
    }
  }, [posts, drafts, inProgress, postErrorFunction]);

  const telepromterAction = useCallback((post) => {
    setShowTelepromter(true);
    setEditPost(post);
  }, []);


  const shareAction = useCallback((postId) => {
      setShowShare(true);
      setSharePostId(postId);
  }, []);

  return (
    <>
      <Modal show={showForceDelete} onHide={() => setShowForceDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            {t("Backend.Post.ForceDelete.Title", "Delete post anyway?")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" style={{ fontSize: 13, marginBottom: 0 }}>
            {forceDeleteDetails || t("Backend.Post.ForceDelete.DefaultDetails", "This post is still referenced by other records.")}
          </Alert>
          <p style={{ marginTop: 12, fontSize: 13, color: "#374151" }}>
            {t("Backend.Post.ForceDelete.Confirm", "All connected records (e.g. group shares and comments) will also be deleted. This cannot be undone.")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForceDelete(false)} style={{ borderRadius: 8, fontSize: 13 }}>
            {t("Backend.Buttons.Cancel", "Cancel")}
          </Button>
          <Button variant="danger" onClick={forceDeleteAction} style={{ borderRadius: 8, fontSize: 13 }}>
            {t("Backend.Post.ForceDelete.Button", "Delete anyway")}
          </Button>
        </Modal.Footer>
      </Modal>

      <ShareModal show={showShare} setShow={setShowShare} sharePostId={sharePostId}/>
      <PostAssistant
        show={showPostAssistant}
        setShow={setShowPostAssistant}
        setShowEdit={setShowEdit}
        posts={drafts}
        setPosts={setDrafts}
        setEditPost={setEditPost}
        showSpinner={showGenerating}
        setShowSpinner={setShowGenerating}
      />
      <VariantsModal
        show={showVariants}
        setShow={setShowVariants}
        post={editPost}
        dataList={drafts}
        setDataList={setDrafts}
        activeKey={activeKey}
        setActiveKey={setActiveKey}
      />
      <LoadingModal
        showSpinner={showPosting}
        setShowSpinner={setShowPosting}
        text={t("Backend.Post.Publishing")}
      />
      <LoadingModal
        showSpinner={showGenerating}
        setShowSpinner={setShowGenerating}
        text={t("Backend.Post.Assistant.GeneratingPost")}
      />
      {editPost && (
        <PostFormView
          showEdit={showEdit}
          setShowEdit={setShowEdit}
          dataList={drafts}
          setDataList={setDrafts}
          editData={editPost}
          setEditData={setEditPost}
          setTrigger={setTrigger}
          trigger={trigger}
        />
      )}
      <TelepromterModal
        post={editPost}
        show={showTeleprompter}
        setShow={setShowTelepromter}
      />
      <h4 className="mb-sm-0"><FontAwesomeIcon icon={faPenToSquare} style={{ color: "#038edc", marginRight: 10, fontSize: 18 }} />Content</h4>

      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-end gap-2" style={{ background: "transparent", borderBottom: "1px solid #eaecf0", padding: "12px 16px" }}>
          <button
            type="button"
            onClick={() => setShowPostAssistant(true)}
            style={{
              padding: "6px 14px", borderRadius: 50, border: "none",
              background: "linear-gradient(135deg,#564ab1,#3d3490)",
              color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: "0 3px 10px rgba(86,74,177,0.3)",
            }}
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: 12 }} />
            AI Post
          </button>
          <AddButton
            addFunction={async () => {
              let response = await crudApi.createNew(SocialEndpoints.posts.new, {});
              if (response.ok) {
                listHandler.newToList(response, posts, setPosts, null, true);
                setEditPost(response.data);
                setShowEdit(true);
              }
            }}
          />
        </Card.Header>
        <CardBody>
          <Tabs
            defaultActiveKey="drafts"
            id="uncontrolled-tab-example"
            className="mb-3 mt-3"
          >
            <Tab
              eventKey="drafts"
              title={<TabTitle icon="uil-file-edit-alt" label={t("Backend.Post.Tabs.Drafts")} />}
            >
              <div className="verti-timeline left-timeline">
                <TimnelineTab
                  posts={drafts}
                  editAction={editAction}
                  deleteAction={deleteAction}
                  setDraft={setDraft}
                  setEditPost={setEditPost}
                  setShowVariants={setShowVariants}
                  publish={publish}
                  setPosts={setDrafts}
                  markInProgress={markInProgress}
                  setActiveKey={setActiveKey}
                  telepromterAction={telepromterAction}
                  shareAction={shareAction}
                />
              </div>
            </Tab>
            <Tab
              eventKey="inprogress"
              title={<TabTitle icon="uil-process" label={t("Backend.Post.Tabs.InProgress")} />}
            >
              <div className="verti-timeline left-timeline">
                <TimnelineTab
                  posts={inProgress}
                  editAction={editAction}
                  deleteAction={deleteAction}
                  setDraft={setDraft}
                  setEditPost={setEditPost}
                  setShowVariants={setShowVariants}
                  publish={publish}
                  setPosts={setInProgress}
                  markInProgress={markInProgress}
                  setActiveKey={setActiveKey}
                  telepromterAction={telepromterAction}
                  shareAction={shareAction}
                />
              </div>
            </Tab>
            <Tab
              eventKey="unschedule"
              title={<TabTitle icon="uil-check-circle" label={t("Backend.Post.Tabs.ReadyUnschedule")} />}
            >
              <div className="verti-timeline left-timeline">
                <TimnelineTab
                  posts={unscheduled}
                  editAction={editAction}
                  deleteAction={deleteAction}
                  setDraft={setDraft}
                  setEditPost={setEditPost}
                  setShowVariants={setShowVariants}
                  publish={publish}
                  setPosts={setUnscheduled}
                  markInProgress={markInProgress}
                  setActiveKey={setActiveKey}
                  telepromterAction={telepromterAction}
                  shareAction={shareAction}
                />
              </div>
            </Tab>
            <Tab
              eventKey="planned"
              title={<TabTitle icon="uil-calendar-alt" label={t("Backend.Post.Tabs.Ready")} />}
            >
              <div className="verti-timeline left-timeline">
                <TimnelineTab
                  posts={planned}
                  editAction={editAction}
                  deleteAction={deleteAction}
                  setDraft={setDraft}
                  setEditPost={setEditPost}
                  setShowVariants={setShowVariants}
                  publish={publish}
                  setPosts={setPlanned}
                  useSchedule={true}
                  markInProgress={markInProgress}
                  setActiveKey={setActiveKey}
                  telepromterAction={telepromterAction}
                  shareAction={shareAction}
                />
              </div>
            </Tab>
            <Tab
              eventKey="published"
              title={<TabTitle icon="uil-rocket" label={t("Backend.Post.Tabs.Published")} />}
            >
              <div className="verti-timeline left-timeline">
                <TimnelineTab
                  posts={published}
                  editAction={editAction}
                  deleteAction={deleteAction}
                  setDraft={setDraft}
                  setEditPost={setEditPost}
                  setShowVariants={setShowVariants}
                  publish={publish}
                  setPosts={setPublished}
                  useSchedule={true}
                  markInProgress={markInProgress}
                  setActiveKey={setActiveKey}
                  telepromterAction={telepromterAction}
                  shareAction={shareAction}
                />
              </div>
            </Tab>
          </Tabs>
        </CardBody>
      </Card>
    </>
  );
};

export default Timeline;
